import { useState } from 'react';

export const useNodeTree = (toast, nodes) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [showDialog, setShowDialog] = useState(0);
  const [dialogType, setDialogType] = useState('add');
  const [question, setQuestion] = useState('');
  const [response, setResponse] = useState('');
  const [expandedKeys, setExpandedKeys] = useState({});
  const [pathNodes, setPathNodes] = useState(new Set());

  const findParent = (tree, targetKey) => {
    for (const item of tree) {
      if (item.children) {
        for (const child of item.children) {
          if (child.key === targetKey) {
            return item;
          }
          const found = findParent([child], targetKey);
          if (found) return found;
        }
      }
    }
    return null;
  };

  const updateNode = (tree, key, updatedNode) => {
    return tree.map(node => {
      if (node.key === key) return updatedNode;
      if (node.children) {
        return { ...node, children: updateNode(node.children, key, updatedNode) };
      }
      return node;
    });
  };

  const addChildNode = (tree, key, childNode) => {
    return tree.map(node => {
      if (node.key === key) {
        return {
          ...node,
          children: [...(node.children || []), childNode]
        };
      }
      if (node.children) {
        return { ...node, children: addChildNode(node.children, key, childNode) };
      }
      return node;
    });
  };

  const removeNode = (tree, key) => {
    return tree.filter(node => {
      if (node.key === key) return false;
      if (node.children) {
        node.children = removeNode(node.children, key);
      }
      return true;
    });
  };

  const getPathToNode = (node) => {
    const path = new Set();
    let current = node;
    while (current) {
      path.add(current.key);
      current = findParent(nodes, current.key);
    }
    return path;
  };

  const expandToNode = (node) => {
    if (!node) return;

    const keys = {};
    let current = node;
    while (current) {
      keys[current.key] = true;
      current = findParent(nodes, current.key);
    }
    setExpandedKeys(keys);
    setPathNodes(getPathToNode(node));
  };

  const handleSaveNode = (nodes, setNodes) => {
    const isRootNode = !selectedNode || selectedNode.key === '0';
    
    if (!question) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Missing field',
        detail: 'Please enter a user message.'
      });
      return;
    }

    if (!isRootNode && !response) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Missing field',
        detail: 'Please enter a bot response.'
      });
      return;
    }

    if (dialogType === 'edit') {
      const updated = updateNode(nodes, selectedNode.key, {
        ...selectedNode,
        label: question,
        data: { question, response: isRootNode ? '' : response }
      });
      setNodes(updated);
    } else {
      if (!selectedNode) {
        const newNode = {
          key: '0',
          label: question,
          data: { question, response: '' },
          children: []
        };
        setNodes([newNode]);
      } else {
        if (selectedNode.children?.length >= 6) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Maximum children reached',
            detail: 'You can only add up to 6 options per question.'
          });
          return;
        }

        const newNode = {
          key: `${selectedNode.key}-${selectedNode.children?.length || 0}`,
          label: question,
          data: { question, response }
        };

        const updated = addChildNode(nodes, selectedNode.key, newNode);
        setNodes(updated);
        
        // Expand the parent node to show the newly added child
        const keys = { ...expandedKeys };
        keys[selectedNode.key] = true;
        setExpandedKeys(keys);
      }
    }

    setShowDialog(0);
    setQuestion('');
    setResponse('');
  };

  return {
    selectedNode,
    setSelectedNode,
    showDialog,
    setShowDialog,
    dialogType,
    setDialogType,
    question,
    setQuestion,
    response,
    setResponse,
    expandedKeys,
    setExpandedKeys,
    pathNodes,
    setPathNodes,
    handleSaveNode,
    updateNode,
    addChildNode,
    removeNode,
    expandToNode
  };
}; 